import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PageMeta from '../components/PageMeta';
import FadeIn from '../components/FadeIn';

const items = [
  { id: 1,  category: 'weddings',        height: 480, src: 'https://picsum.photos/seed/wedding1/600/900',    alt: 'Bride and groom during outdoor wedding ceremony in Christchurch' },
  { id: 2,  category: 'portraits',       height: 360, src: 'https://picsum.photos/seed/portrait1/600/900',   alt: 'Natural light portrait session, Christchurch New Zealand' },
  { id: 3,  category: 'engagements',     height: 420, src: 'https://picsum.photos/seed/engagement1/600/900', alt: 'Couple engagement shoot at sunset, New Zealand' },
  { id: 4,  category: 'indian-weddings', height: 500, src: 'https://picsum.photos/seed/indian1/600/900',     alt: 'Indian wedding ceremony with vibrant floral mandap, New Zealand' },
  { id: 5,  category: 'weddings',        height: 360, src: 'https://picsum.photos/seed/wedding2/600/900',    alt: 'First dance at wedding reception, Christchurch' },
  { id: 6,  category: 'corporate',       height: 300, src: 'https://picsum.photos/seed/corporate1/600/900',  alt: 'Corporate headshot and professional portrait, Christchurch' },
  { id: 7,  category: 'weddings',        height: 440, src: 'https://picsum.photos/seed/wedding3/600/900',    alt: 'Bride portrait in soft morning window light' },
  { id: 8,  category: 'engagements',     height: 380, src: 'https://picsum.photos/seed/couple1/600/900',     alt: 'Engagement session in botanical garden, Christchurch' },
  { id: 9,  category: 'portraits',       height: 460, src: 'https://picsum.photos/seed/portrait2/600/900',   alt: 'Family portrait session in Canterbury, New Zealand' },
  { id: 10, category: 'indian-weddings', height: 400, src: 'https://picsum.photos/seed/indian2/600/900',     alt: 'Mehndi ceremony candid moment, Indian wedding New Zealand' },
  { id: 11, category: 'weddings',        height: 320, src: 'https://picsum.photos/seed/wedding4/600/900',    alt: 'Wedding ceremony floral detail — bouquet and rings' },
  { id: 12, category: 'corporate',       height: 350, src: 'https://picsum.photos/seed/corporate2/600/900',  alt: 'Corporate event photography, Christchurch New Zealand' },
  { id: 13, category: 'engagements',     height: 440, src: 'https://picsum.photos/seed/couple2/600/900',     alt: 'Engagement portrait on Canterbury plains, New Zealand' },
  { id: 14, category: 'indian-weddings', height: 480, src: 'https://picsum.photos/seed/indian3/600/900',     alt: 'Indian bridal portrait with traditional jewellery and attire' },
  { id: 15, category: 'portraits',       height: 320, src: 'https://picsum.photos/seed/portrait3/600/900',   alt: 'Individual portrait session, golden hour lighting New Zealand' },
  { id: 16, category: 'weddings',        height: 400, src: 'https://picsum.photos/seed/wedding5/600/900',    alt: 'Candid wedding moment — bridal party laughing, Christchurch' },
  { id: 17, category: 'corporate',       height: 280, src: 'https://picsum.photos/seed/corporate3/600/900',  alt: 'Team corporate photography, Christchurch New Zealand' },
  { id: 18, category: 'engagements',     height: 360, src: 'https://picsum.photos/seed/couple3/600/900',     alt: 'Couple walking on Christchurch waterfront engagement session' },
  { id: 19, category: 'indian-weddings', height: 420, src: 'https://picsum.photos/seed/indian4/600/900',     alt: 'Sangeet dance celebration at Indian wedding, New Zealand' },
  { id: 20, category: 'portraits',       height: 480, src: 'https://picsum.photos/seed/portrait4/600/900',   alt: 'Portrait session in natural outdoor surroundings, New Zealand' },
  { id: 21, category: 'weddings',        height: 360, src: 'https://picsum.photos/seed/wedding6/600/900',    alt: 'Wedding reception speeches, genuine emotion captured' },
  { id: 22, category: 'corporate',       height: 320, src: 'https://picsum.photos/seed/corporate4/600/900',  alt: 'Product launch event photography, professional setting' },
  { id: 23, category: 'engagements',     height: 400, src: 'https://picsum.photos/seed/couple4/600/900',     alt: 'Engagement session in vineyard at golden hour, New Zealand' },
  { id: 24, category: 'indian-weddings', height: 360, src: 'https://picsum.photos/seed/indian5/600/900',     alt: 'Baraat procession at Indian wedding celebration, New Zealand' },
];

const filters = [
  { key: 'all',            label: 'All' },
  { key: 'weddings',       label: 'Weddings' },
  { key: 'indian-weddings',label: 'Indian Weddings' },
  { key: 'engagements',    label: 'Engagements' },
  { key: 'portraits',      label: 'Portraits' },
  { key: 'corporate',      label: 'Corporate' },
];

export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState('all');

  const filtered = activeFilter === 'all'
    ? items
    : items.filter((item) => item.category === activeFilter);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <PageMeta
        title="Portfolio | Dhrumil Desai Photography Christchurch"
        description="Browse the wedding, portrait, engagement, and corporate photography portfolio of Dhrumil Desai, Christchurch's premier wedding photographer. Available NZ wide."
      />

      {/* ── Page Header ── */}
      <section className="pt-40 pb-20 px-6 md:px-12 bg-white text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <p
            className="text-[10px] tracking-[0.4em] uppercase text-bronze mb-5 font-inter"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Our Work
          </p>
          <h1
            className="font-playfair text-4xl md:text-6xl text-charcoal leading-tight"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Stories we've had<br />the honour to tell
          </h1>
          <p
            className="font-inter font-light text-sm md:text-base text-gray-500 mt-6 max-w-md mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            A selection of weddings, portraits, engagements, and corporate work from across New Zealand.
          </p>
        </motion.div>
      </section>

      {/* ── Filter Tabs ── */}
      <section className="px-6 md:px-12 bg-white" aria-label="Portfolio filter">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-x-8 gap-y-3 border-b border-gray-100 pb-1">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`text-[11px] tracking-[0.25em] uppercase font-inter pb-4 border-b-[1.5px] transition-all duration-200 ${
                  activeFilter === f.key
                    ? 'border-bronze text-bronze'
                    : 'border-transparent text-gray-400 hover:text-charcoal'
                }`}
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
                aria-pressed={activeFilter === f.key}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Masonry Grid ── */}
      <section className="py-12 px-6 md:px-12 bg-white" aria-label="Portfolio images">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-4"
            >
              {filtered.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.04, ease: 'easeOut' }}
                  className="break-inside-avoid mb-3 md:mb-4 overflow-hidden group"
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    style={{ height: item.height, display: 'block' }}
                    loading="lazy"
                    draggable="false"
                  />
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-28 px-6 md:px-12 bg-cream text-center" aria-labelledby="portfolio-cta">
        <FadeIn className="max-w-xl mx-auto">
          <h2
            id="portfolio-cta"
            className="font-playfair text-3xl md:text-4xl text-charcoal mb-6 leading-tight"
            style={{ fontFamily: '"Playfair Display", Georgia, serif' }}
          >
            Like what you see?
          </h2>
          <p
            className="font-inter font-light text-sm text-gray-500 mb-10 leading-relaxed"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Reach out to check availability for your date and to learn more about what's possible for your wedding or portrait session.
          </p>
          <Link
            to="/contact"
            className="border border-charcoal px-12 py-4 text-[11px] tracking-[0.25em] uppercase font-inter text-charcoal hover:bg-charcoal hover:text-white transition-all duration-300 inline-block"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            Book a Session
          </Link>
        </FadeIn>
      </section>
    </motion.main>
  );
}
